import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {YogaNodeProps} from '@wonderlandengine/react-ui';
import React, {forwardRef, useEffect, useRef, useMemo} from 'react';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {Container} from '@wonderlandengine/react-ui/components';
import {MalFacialAnimationUi} from '../../components/mal-facial-animation-ui.js';

const forwardVec = Float32Array.from([0, 1000, 0]);

export class MalUIComponent extends Component {
    static TypeName = 'mal-ui-component';

    @property.object()
    malMenuRoot?: Object3D;

    @property.vector3(0, 0, 0)
    additionalRotation = [0, 0, 0];

    private _malParent: Object3D;

    private _malFacialAnimation: MalFacialAnimationUi;
    private _isStarted = false;

    start(): void {
        this._malParent = this.object.addChild();
    }
    updateRotation() {
        if (this.additionalRotation[0] !== 0) {
            this._malParent.rotateAxisAngleDegLocal([1, 0, 0], this.additionalRotation[0]);
        }
        if (this.additionalRotation[1] !== 0) {
            this._malParent.rotateAxisAngleDegLocal([0, 1, 0], this.additionalRotation[1]);
        }
        if (this.additionalRotation[2] !== 0) {
            this._malParent.rotateAxisAngleDegLocal([0, 0, 1], this.additionalRotation[2]);
        }
    }
    actuallyStart() {
        // by waiting until the first update all calculations are done and we actually
        // can start doing things...
        const malRootClone = this.malMenuRoot.clone();
        malRootClone.parent = this._malParent;
        this._malFacialAnimation = malRootClone.getComponent(MalFacialAnimationUi);
        malRootClone.resetPositionRotation();
        malRootClone.resetScaling();
        this.object.lookAt(forwardVec);
        this._malParent.rotateAxisAngleDegLocal([0, 1, 0], 180);
        this.updateRotation();
        this._malFacialAnimation.active = true;
    }

    private _checkEveryNFrames: number = 2;
    private _frameCounter: number = this._checkEveryNFrames;

    update() {
        if (!this._isStarted) {
            this._isStarted = true;
            this.actuallyStart();
        }

        this._frameCounter++;
        if (this._frameCounter >= this._checkEveryNFrames) {
            this._frameCounter = 0;
            this.engine.scene.views[0].object.getPositionWorld(forwardVec);
            this.object.lookAt(forwardVec);
        }
    }
}

export const MalUI = forwardRef<
    Object3D,
    YogaNodeProps & {
        additionalRotation?: [number, number, number];
    }
>((props, ref) => {
    const meshParent = useRef<Object3D>(null);
    const malUIComponent = useRef<MalUIComponent>(null);
    const rotbMC = useRotBMaterialContext();

    const down = () => {
        // if (sliderComponent.current) {
        //     sliderComponent.current.down();
        // }
    };

    useEffect(() => {
        if (meshParent.current === null) {
            return;
        }
        const mesh = meshParent.current.findByName('mesh')[0];
        malUIComponent.current =
            mesh.getComponent(MalUIComponent) ??
            mesh.addComponent(MalUIComponent, {
                malMenuRoot: rotbMC.malReference,
                additionalRotation: props.additionalRotation ?? [0, 0, 0],
            });
        if (props.additionalRotation) {
            malUIComponent.current.updateRotation();
        }
    }, [props]);

    const meshComponent = useMemo(() => {
        return React.createElement('mesh', {
            ref: meshParent,
            ...props,
        });
    }, []);

    return (
        <Container onDown={down} {...props}>
            {meshComponent}
        </Container>
    );
});

MalUI.displayName = 'MalUI';
